import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaSearch,
  FaSort,
  FaEllipsisV,
  FaSpinner,
  FaUtensils,
} from 'react-icons/fa';
import api from '../../services/api';
const CustomerSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionsPerPage, setSubscriptionsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('subscribedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage, searchTerm, subscriptionsPerPage, sortBy, sortOrder]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/customers/subscriptions', {
        params: {
          page: currentPage,
          limit: subscriptionsPerPage,
          search: searchTerm,
          sortBy,
          sortOrder,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('customerToken')}`,
        },
      });
      setSubscriptions(response.data.subscriptions);
      setTotalPages(response.data.totalPages);
      setTotalSubscriptions(response.data.totalSubscriptions);
    } catch (error) {
      toast.error('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (restaurantId) => {
    try {
      await api.post(
        `/customers/unsubscribe/${restaurantId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('customerToken')}`,
          },
        }
      );
      toast.success('Unsubscribed successfully');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to unsubscribe');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (field === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const renderSubscriptionCard = (subscription) => (
    <div
      key={subscription._id}
      className='bg-white rounded-lg shadow-md p-4 mb-4'
    >
      <h3 className='text-lg font-semibold mb-2'>
        {/* {subscription.restaurant.name} */}
        {subscription.restaurant
          ? subscription.restaurant.name
          : 'Unavailable Restaurant'}
      </h3>
      <p className='text-sm text-gray-600 mb-2'>
        Subscribed: {new Date(subscription.subscribedAt).toLocaleDateString()}
      </p>
      <div className='flex justify-between items-center'>
        {/* <Link
          to={`/${subscription.restaurant._id}`}
          className='text-blue-500 hover:text-blue-700 text-sm'
        >
          View Details
        </Link> */}
        {subscription.restaurant ? (
          <Link
            to={`/${subscription.restaurant._id}`}
            className='text-blue-500 hover:text-blue-700 text-sm'
          >
            View Details
          </Link>
        ) : (
          <span className='text-gray-400 text-sm'>Details Unavailable</span>
        )}
        <button
          onClick={() => handleUnsubscribe(subscription.restaurant._id)}
          className='bg-red-500 hover:bg-red-700 text-white text-sm font-bold py-1 px-2 rounded'
        >
          Unsubscribe
        </button>
      </div>
    </div>
  );

  const renderPagination = () => (
    <div className='flex justify-center items-center space-x-2 mt-4'>
      <button
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
        className='px-3 py-1 bg-gray-200 rounded-md disabled:opacity-50'
      >
        Prev
      </button>
      <span className='text-sm'>
        Page {currentPage} of {totalPages}
      </span>
      <button
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
        className='px-3 py-1 bg-gray-200 rounded-md disabled:opacity-50'
      >
        Next
      </button>
    </div>
  );

  const renderEmptyState = () => (
    <div className='text-center py-10'>
      <FaUtensils className='text-6xl text-gray-300 mx-auto mb-4' />
      <p className='text-xl text-gray-500'>
        You haven't subscribed to any restaurants yet.
      </p>
      <p className='text-gray-400 mt-2'>
        Subscribe to restaurants to start receiving updates!
      </p>
    </div>
  );
  return (
    <div className='container mx-auto px-4 py-8 max-w-4xl'>
      <h1 className='text-2xl md:text-3xl font-bold mb-6'>My Subscriptions</h1>

      <div className='mb-6 flex flex-col md:flex-row md:items-center md:justify-between'>
        <div className='relative flex-grow mb-4 md:mb-0 md:mr-4'>
          <input
            type='text'
            placeholder='Search subscriptions...'
            value={searchTerm}
            onChange={handleSearch}
            className='w-full pl-10 pr-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          <FaSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
        </div>
        {subscriptions.length > 0 && (
          <div className='flex items-center'>
            <label htmlFor='sortBy' className='mr-2 text-sm'>
              Sort by:
            </label>
            <select
              id='sortBy'
              value={sortBy}
              onChange={(e) => handleSort(e.target.value)}
              className='p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value='restaurant.name'>Restaurant Name</option>
              <option value='subscribedAt'>Subscription Date</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className='ml-2 p-2 bg-gray-200 rounded-md'
            >
              <FaSort />
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className='flex justify-center items-center h-64'>
          <FaSpinner className='animate-spin text-4xl text-blue-500' />
        </div>
      ) : subscriptions.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          <div className='md:hidden'>
            {subscriptions.map(renderSubscriptionCard)}
          </div>

          <div className='hidden md:block overflow-x-auto'>
            <table className='min-w-full bg-white'>
              <thead>
                <tr>
                  <th className='px-4 py-2 text-left'>Restaurant Name</th>
                  <th className='px-4 py-2 text-left'>Subscribed At</th>
                  <th className='px-4 py-2 text-left'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription) => (
                  <tr key={subscription._id} className='border-b'>
                    <td className='px-4 py-2'>
                      {/* {subscription.restaurant.name} */}
                      {subscription.restaurant
                        ? subscription.restaurant.name
                        : 'Unavailable Restaurant'}
                    </td>
                    <td className='px-4 py-2'>
                      {new Date(subscription.subscribedAt).toLocaleDateString()}
                    </td>
                    <td className='px-4 py-2'>
                      {/* <Link
                        to={`/${subscription.restaurant._id}`}
                        className='text-blue-500 hover:text-blue-700 mr-2'
                      >
                        View Details
                      </Link> */}
                      {subscription.restaurant ? (
                        <Link
                          to={`/${subscription.restaurant._id}`}
                          className='text-blue-500 hover:text-blue-700 mr-2'
                        >
                          View Details
                        </Link>
                      ) : (
                        <span className='text-gray-400 mr-2'>
                          Details Unavailable
                        </span>
                      )}
                      <button
                        onClick={() =>
                          handleUnsubscribe(subscription.restaurant._id)
                        }
                        className='bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded'
                      >
                        Unsubscribe
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {renderPagination()}

          <div className='mt-4 text-sm text-gray-600 flex flex-col md:flex-row md:justify-between md:items-center'>
            <div className='mb-2 md:mb-0'>
              <label htmlFor='subscriptionsPerPage' className='mr-2'>
                Show:
              </label>
              <select
                id='subscriptionsPerPage'
                value={subscriptionsPerPage}
                onChange={(e) =>
                  setSubscriptionsPerPage(Number(e.target.value))
                }
                className='p-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              >
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
              </select>
            </div>
            <div>
              Showing {(currentPage - 1) * subscriptionsPerPage + 1} -{' '}
              {Math.min(currentPage * subscriptionsPerPage, totalSubscriptions)}{' '}
              of {totalSubscriptions} subscriptions
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerSubscriptions;
// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { Link } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Pagination from '../restaurants/Pagination';

// const CustomerSubscriptions = () => {
//   const [subscriptions, setSubscriptions] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(0);
//   const [totalSubscriptions, setTotalSubscriptions] = useState(0);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [subscriptionsPerPage, setSubscriptionsPerPage] = useState(10);
//   const [sortBy, setSortBy] = useState('subscribedAt');
//   const [sortOrder, setSortOrder] = useState('desc');

//   useEffect(() => {
//     fetchSubscriptions();
//   }, [currentPage, searchTerm, subscriptionsPerPage, sortBy, sortOrder]);

//   const fetchSubscriptions = async () => {
//     try {
//       setLoading(true);
//       const response = await axios.get('/api/customers/subscriptions', {
//         params: {
//           page: currentPage,
//           limit: subscriptionsPerPage,
//           search: searchTerm,
//           sortBy,
//           sortOrder,
//         },
//         headers: {
//           Authorization: `Bearer ${localStorage.getItem('customerToken')}`,
//         },
//       });
//       setSubscriptions(response.data.subscriptions);
//       setTotalPages(response.data.totalPages);
//       setTotalSubscriptions(response.data.totalSubscriptions);
//     } catch (error) {
//       toast.error('Failed to fetch subscriptions');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleUnsubscribe = async (restaurantId) => {
//     try {
//       await axios.post(
//         `/api/customers/unsubscribe/${restaurantId}`,
//         {},
//         {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem('customerToken')}`,
//           },
//         }
//       );
//       toast.success('Unsubscribed successfully');
//       fetchSubscriptions();
//     } catch (error) {
//       toast.error('Failed to unsubscribe');
//     }
//   };

//   const handleSearch = (e) => {
//     setSearchTerm(e.target.value);
//     setCurrentPage(1);
//   };

//   const handleSubscriptionsPerPageChange = (e) => {
//     setSubscriptionsPerPage(Number(e.target.value));
//     setCurrentPage(1);
//   };

//   const handleSort = (field) => {
//     if (field === sortBy) {
//       setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
//     } else {
//       setSortBy(field);
//       setSortOrder('asc');
//     }
//   };

//   return (
//     <div className='container mx-auto px-4 py-8'>
//       <h1 className='text-3xl font-bold mb-6'>My Subscriptions</h1>
//       <div className='mb-4'>
//         <input
//           type='text'
//           placeholder='Search subscriptions...'
//           value={searchTerm}
//           onChange={handleSearch}
//           className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
//         />
//       </div>
//       {loading ? (
//         <div className='flex justify-center items-center h-64'>
//           <div className='animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500'></div>
//         </div>
//       ) : (
//         <>
//           <div className='overflow-x-auto'>
//             <table className='min-w-full bg-white'>
//               <thead>
//                 <tr>
//                   <th
//                     className='px-4 py-2 cursor-pointer'
//                     onClick={() => handleSort('restaurant.name')}
//                   >
//                     Restaurant Name{' '}
//                     {sortBy === 'restaurant.name' &&
//                       (sortOrder === 'asc' ? '▲' : '▼')}
//                   </th>
//                   <th
//                     className='px-4 py-2 cursor-pointer'
//                     onClick={() => handleSort('subscribedAt')}
//                   >
//                     Subscribed At{' '}
//                     {sortBy === 'subscribedAt' &&
//                       (sortOrder === 'asc' ? '▲' : '▼')}
//                   </th>
//                   <th className='px-4 py-2'>Actions</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {subscriptions.map((subscription) => (
//                   <tr key={subscription._id}>
//                     <td className='border px-4 py-2'>
//                       {subscription.restaurant.name}
//                     </td>
//                     <td className='border px-4 py-2'>
//                       {new Date(subscription.subscribedAt).toLocaleDateString()}
//                     </td>
//                     <td className='border px-4 py-2'>
//                       <Link
//                         to={`/${subscription.restaurant._id}`}
//                         className='text-blue-500 hover:text-blue-700 mr-2'
//                       >
//                         View Details
//                       </Link>
//                       <button
//                         onClick={() =>
//                           handleUnsubscribe(subscription.restaurant._id)
//                         }
//                         className='bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded'
//                       >
//                         Unsubscribe
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//           <Pagination
//             currentPage={currentPage}
//             totalPages={totalPages}
//             onPageChange={setCurrentPage}
//           />
//           <div className='flex justify-between items-center mt-4 text-sm text-gray-600'>
//             <div>
//               <span className='mr-2'>Subscriptions per page:</span>
//               <select
//                 value={subscriptionsPerPage}
//                 onChange={handleSubscriptionsPerPageChange}
//                 className='p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
//               >
//                 <option value='10'>10</option>
//                 <option value='20'>20</option>
//                 <option value='50'>50</option>
//               </select>
//             </div>
//             <div>
//               Showing {(currentPage - 1) * subscriptionsPerPage + 1} -{' '}
//               {Math.min(currentPage * subscriptionsPerPage, totalSubscriptions)}{' '}
//               of {totalSubscriptions} subscriptions
//             </div>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default CustomerSubscriptions;

// // components/CustomerSubscriptions.js
// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { Link } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Pagination from '../restaurants/Pagination'; // Import the Pagination component

// const CustomerSubscriptions = () => {
//   const [subscriptions, setSubscriptions] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(0);
//   const [searchTerm, setSearchTerm] = useState('');

//   useEffect(() => {
//     fetchSubscriptions();
//   }, [currentPage, searchTerm]);

//   const fetchSubscriptions = async () => {
//     try {
//       setLoading(true);
//       const response = await axios.get('/api/customers/subscriptions', {
//         params: { page: currentPage, search: searchTerm },
//         headers: {
//           Authorization: `Bearer ${localStorage.getItem('customerToken')}`,
//         },
//       });
//       console.log(response);
//       setSubscriptions(response.data);
//       setTotalPages(response.data.totalPages);
//     } catch (error) {
//       toast.error('Failed to fetch subscriptions');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleUnsubscribe = async (restaurantId) => {
//     try {
//       await axios.post(
//         `/api/customers/unsubscribe/${restaurantId}`,
//         {},
//         {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem('customerToken')}`,
//           },
//         }
//       );
//       toast.success('Unsubscribed successfully');
//       fetchSubscriptions();
//     } catch (error) {
//       toast.error('Failed to unsubscribe');
//     }
//   };

//   return (
//     <div className='container mx-auto px-4 py-8'>
//       <h1 className='text-3xl font-bold mb-6'>My Subscriptions</h1>
//       <div className='mb-4'>
//         <input
//           type='text'
//           placeholder='Search subscriptions...'
//           value={searchTerm}
//           onChange={(e) => setSearchTerm(e.target.value)}
//           className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
//         />
//       </div>
//       {loading ? (
//         <div>Loading...</div>
//       ) : (
//         <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
//           {subscriptions?.map((subscription) => (
//             <div key={subscription._id} className='border p-4 rounded-lg'>
//               <h2 className='text-xl font-bold mb-2'>
//                 {subscription.restaurant.name}
//               </h2>
//               <Link
//                 to={`/${subscription.restaurant._id}`}
//                 className='text-blue-500 hover:text-blue-700'
//               >
//                 View Details
//               </Link>
//               <button
//                 onClick={() => handleUnsubscribe(subscription.restaurant._id)}
//                 className='ml-4 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
//               >
//                 Unsubscribe
//               </button>
//             </div>
//           ))}
//         </div>
//       )}
//       <Pagination
//         currentPage={currentPage}
//         totalPages={totalPages}
//         onPageChange={setCurrentPage}
//       />
//       <div className='flex justify-between items-center mt-4 text-sm text-gray-600'>
//         {/* <div className='flex flex-col sm:flex-row justify-between items-center mt-6 text-sm text-gray-600'> */}
//         <div>
//           <span className='mr-2'>Coupons per page:</span>
//           <select
//             value={filters.couponsPerPage}
//             onChange={handleCouponsPerPageChange}
//             className='p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
//           >
//             <option value='6'>6</option>
//             <option value='12'>12</option>
//             <option value='18'>18</option>
//             <option value='30'>30</option>
//           </select>
//         </div>
//         <div>
//           Showing {(pagination.currentPage - 1) * filters.couponsPerPage + 1} -{' '}
//           {Math.min(
//             pagination.currentPage * filters.couponsPerPage,
//             pagination.totalCoupons
//           )}{' '}
//           of {pagination.totalCoupons} coupons
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CustomerSubscriptions;
